import { createTheme, responsiveFontSizes } from "@material-ui/core/styles";
import merge from "lodash/merge";
import { THEMES } from "../constants";
import { darkShadows, lightShadows } from "./shadows";

const baseOptions = {
  direction: "ltr",
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: "75%",
          width: "75%",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          "& .MuiTouchRipple-root .MuiTouchRipple-child": {
            scale: "123%",
            padding: "0%",
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          height: "100%",
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: "hidden",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "auto",
          marginRight: "16px",
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600,
    },
    fontFamily: "Silka, sans-serif",
    h1: {
      fontFamily: "Sharp Grotesk Medium, sans-serif",
      fontWeight: 500,
      fontSize: "3.5rem",
    },
    h2: {
      fontFamily: "Sharp Grotesk Medium, sans-serif",
      fontWeight: 500,
      fontSize: "3rem",
    },
    h3: {
      fontFamily: "Sharp Grotesk Medium, sans-serif",
      fontWeight: 500,
      fontSize: "2.25rem",
    },
    h4: {
      fontFamily: "Sharp Grotesk Medium, sans-serif",
      fontWeight: 500,
      fontSize: "2rem",
    },
    h5: {
      fontFamily: "Sharp Grotesk Medium, sans-serif",
      fontWeight: 500,
      fontSize: "1.5rem",
    },
    h6: {
      fontFamily: "Sharp Grotesk Medium, sans-serif",
      fontWeight: 500,
      fontSize: "1.125rem",
    },
    overline: {
      fontWeight: 600,
    },
  },
};

const themesOptions = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              opacity: 0.86,
              color: "#0b4356",
            },
          },
        },
      },
    },
    palette: {
      action: {
        active: "#ff7427",
      },
      background: {
        default: "#f4f5f7",
        paper: "#ffffff",
        extra: "#F4F5F7",
      },
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
      },
      mode: "light",
      primary: {
        contrastText: "#ffffff",
        main: "#0b4356",
      },
      secondary: {
        main: "#f4f5f7",
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
      },
      text: {
        primary: "#172b4d",
        secondary: "#6b778c",
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
      },
    },
    shadows: lightShadows,
  },
  [THEMES.DARK]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
    },
    palette: {
      background: {
        default: "#171c24",
        paper: "#222b36",
        extra: "#171c24",
      },
      divider: "rgba(145, 158, 171, 0.24)",
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
      },
      mode: "dark",
      primary: {
        contrastText: "#ffffff",
        main: "#688eff",
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
      },
      text: {
        primary: "#ffffff",
        secondary: "#919eab",
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
      },
    },
    shadows: darkShadows,
  },
  [THEMES.NATURE]: {
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid rgba(145, 158, 171, 0.24)",
          },
        },
      },
    },
    palette: {
      background: {
        default: "#1c2531",
        paper: "#293142",
      },
      divider: "rgba(145, 158, 171, 0.24)",
      error: {
        contrastText: "#ffffff",
        main: "#f44336",
      },
      mode: "dark",
      primary: {
        contrastText: "#ffffff",
        main: "#01ab56",
      },
      success: {
        contrastText: "#ffffff",
        main: "#4caf50",
      },
      text: {
        primary: "#ffffff",
        secondary: "#919eab",
      },
      warning: {
        contrastText: "#ffffff",
        main: "#ff9800",
      },
    },
    shadows: darkShadows,
  },
};

export const createDashboardTheme = (config: any = {}) => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      {
        ...(config.roundedCorners && {
          shape: {
            borderRadius: 16,
          },
        }),
      },
      {
        direction: config.direction,
      }
    ) as any
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
